<template>
    <common-modal
        height="large"
        width="extra-large-wide"
        @emitEvent="emitEvent($event, ['close'])"
        @close="emitEvent({ name: 'close' })"
    >
        <div class="referral-tracking__referrals-made-card">
            <common-loader v-if="isLoading" :width="56" :isProcessing="isLoading"></common-loader>
            <p v-else-if="loadError" class="content__error">{{ $t('common.errors.load_failed') }}</p>
            <div v-if="!loadError" class="referral-pool-details-card">
                <div class="referral-pool-details-grid">
                    <div class="referral-pool-details-col">
                        <div class="detail-row">
                            <span class="detail-label">Patient Name:</span>
                            <span class="detail-value">
                                <common-patient-name :patient="referralData?.patient"></common-patient-name>
                            </span>
                        </div>
                        <div class="detail-row">
                            <span class="detail-label">DOB:</span>
                            <span class="detail-value">{{ patientDob }}</span>
                        </div>
                    </div>
                    <div class="referral-pool-details-col">
                        <div class="detail-row">
                            <span class="detail-label">Timeframe:</span>
                            <span class="detail-value">
                                {{ getTimeframeLabel }}
                            </span>
                        </div>
                        <div class="detail-row">
                            <span class="detail-label">{{ specialityOrTestLabel }}</span>
                            <span class="detail-value">
                                <div v-if="hasAdditionalTests(referralData)" class="referral-tracking__stage-tooltip-wrapper">
                                    <div class="test-name">
                                        <p class="name">
                                            {{ getMatchReferralTitle(referralData) }} +{{ getAdditionalTestsCount(referralData) }}
                                        </p>
                                    </div>

                                    <div class="referral-tracking__stage-tooltip">
                                        {{ getAncillaryTestsTooltip(referralData) }}
                                    </div>
                                </div>

                                <p v-else class="name">
                                    {{ getMatchReferralTitle(referralData) }}
                                </p>
                            </span>
                        </div>
                    </div>
                    <div class="referral-pool-details-col">
                        <div class="detail-row">
                            <span class="detail-label">Date of Request:</span>
                            <span class="detail-value">{{ dateOfRequest }}</span>
                        </div>
                        <div class="detail-row">
                            <span class="detail-label">Insurance:</span>
                            <span class="detail-value">{{ insurance }}</span>
                        </div>
                    </div>
                </div>
            </div>
            <table class="referral-tracking__referrals-made-table">
                <thead>
                    <tr>
                        <th>{{ $t('pages.referrals.index.referrals_made.id_made') }}</th>
                        <th v-if="!isMobileLarge">{{ $t('pages.referrals.index.referrals_made.stage') }}</th>
                        <th v-if="!isTablet">{{ $t('pages.referrals.index.headings.status') }}</th>
                        <th v-if="!isTablet">{{ $t('pages.referrals.index.referrals_made.timing') }}</th>
                        <th>{{ $t('pages.referrals.index.referrals_made.action') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="provider in providers" :key="provider.id">
                        <td class="referral-tracking__card-referral-layout">
                            <div class="user-cell">
                                <div class="user-avatar">
                                    <common-user-avatar size="" :user="getAvatarUser(provider)"></common-user-avatar>
                                </div>
                                <div>
                                    <p class="name">{{ getProviderName(provider) }}</p>
                                    <p class="sub">{{ getProviderSpeciality(provider) }}</p>
                                </div>
                            </div>
                        </td>
                        <td v-if="!isMobileLarge">
                            <div class="referral-tracking__stage">
                                <span :class="getStageClasses(provider)" @click="handleStageAction(provider)">
                                    <common-logo-spinner
                                        v-if="isStageLoadingForReferral(provider)"
                                        class="referral-tracking__stage-inline-loader"
                                        :width="36"
                                        color="#fff"
                                    ></common-logo-spinner>
                                    {{ formatStage(provider) }}
                                </span>

                                <div class="referral-tracking__stage-tooltip-wrapper">
                                    <common-icon
                                        name="info"
                                        size="semi-small"
                                        class="referral-tracking__stage-info-icon"
                                    ></common-icon>
                                    <div class="referral-tracking__stage-tooltip">
                                        {{ getStageTooltip(provider) }}
                                    </div>
                                </div>
                            </div>
                        </td>
                        <td v-if="!isTablet">
                            <div class="referral-tracking__status-cell">
                                <span :class="getStatusBadgeClass(provider)">
                                    <span class="referral-tracking__status-content">
                                        <span>{{ getStatusText(provider) }}</span>
                                    </span>
                                </span>
                            </div>
                        </td>
                        <td v-if="!isTablet">
                            <span :class="getTimingClass(provider?.stageInfo)">
                                {{ getTimingText(provider) }}
                            </span>
                        </td>
                        <td class="referral-tracking__view-port">
                            <div class="referral-tracking__confirt-detail">
                                <common-button
                                    v-if="hasPendingAdditionalRecordsRequest(provider)"
                                    :label="$t('modals.patient_record.upload')"
                                    type="primary"
                                    size="medium-small"
                                    @click="uploadRecords(provider)"
                                ></common-button>
                                <button
                                    v-else
                                    class="referral-tracking__view-details-btn"
                                    @click="openDetailsModal('referrals_pool')"
                                >
                                    {{ $t('pages.referrals.index.referrals_made.detail') }}
                                </button>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <referral-modal
            v-if="showViewDetailsModal"
            :showAsInnerModal="true"
            :referral="activeReferral"
            type="made"
            sourceType="referrals_pool"
            :isOpen="showViewDetailsModal"
            :isFetching="isFetching"
            @open-direct-referral="openDirectReferralFromMatchReferal"
            @close="closeDetailsModal"
        ></referral-modal>
        <upload-referral-additional-records
            v-if="showUploadRecordsModal"
            :isOpen="showUploadRecordsModal"
            :provider="selectedProvider"
            :referralPoolId="referralPoolId"
            @close="closeUploadRecordsModal"
            @records-uploaded="handleRecordsUploaded"
        ></upload-referral-additional-records>
    </common-modal>
</template>

<script>
    import ReferralsPoolAPI from 'api/ReferralsPoolAPI'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import StagesEnum from '@/enums/StagesEnum'
    import ReferralStatusEnum from '@/enums/ReferralStatusEnum'
    import ReferralTrackingTypeEnum from '@/enums/ReferralTrackingTypeEnum'
    import UrgencyEnum from '@/enums/UrgencyEnum'
    import TimeWindowSearchCriteriaEnum from '@/enums/TimeWindowSearchCriteriaEnum'
    import ReferralsAPI from 'api/ReferralsAPI'
    import ReferralModal from './ReferralModal.vue'
    import NextActionsEnum from '@/enums/NextActionsEnum'
    import UploadReferralAdditionalRecords from './UploadReferralAdditionalRecords.vue'

    export default {
        mixins: [ScreenSizeDetectionMixin],
        components: {
            ReferralModal,
            UploadReferralAdditionalRecords,
        },

        props: {
            referralPoolId: { type: [Number, String], required: true },
            referralData: { type: Object, default: () => ({}) },
        },
        data() {
            return {
                providers: [],
                isLoading: true,
                loadError: null,
                PracticeTypeEnum,
                showViewDetailsModal: false,
                activeReferral: {},
                isFetching: false,
                isStageLoading: false,
                selectedProviderForActions: {},
                showUploadRecordsModal: false,
                selectedProvider: null,
            }
        },

        computed: {
            usePhysicianLayout() {
                if (!this.providers?.length) return false
                const first = this.providers[0]
                if (!first) return false
                if (first.type === 'external') return true
                return first.practice?.practiceType === PracticeTypeEnum.PHYSICIAN
            },
            referralTypeClass() {
                return () => ({
                    referrals: true,
                    blue: this.referralData?.sourceType === ReferralTrackingTypeEnum.DIRECT,
                    purple: this.referralData?.sourceType === ReferralTrackingTypeEnum.MATCH,
                })
            },
            patientName() {
                const patient = this.referralData?.patient
                if (!patient) return 'N/A'
                const fullName = [patient.firstName, patient.lastName].filter(Boolean).join(' ')
                return fullName || patient.name || 'N/A'
            },
            patientDob() {
                const dob = this.referralData?.patient?.dateOfBirth
                if (!dob) return 'N/A'
                const parts = String(dob).split('-')
                if (parts.length === 3) {
                    const [year, month, day] = parts
                    return `${month}-${day}-${year}`
                }
                return dob
            },
            specialityOrTestLabel() {
                return this.isAncillary(this.referralData) ? 'Tests:' : 'Speciality:'
            },
            dateOfRequest() {
                const date = this.referralData?.createdDateTime || this.referralData?.createdAt
                if (!date) return 'N/A'
                return new Date(date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                })
            },
            getTimeframeLabel() {
                if (!this.referralData) return 'N/A'
                const timeframeVal = this.referralData?.timeframe
                const label = TimeWindowSearchCriteriaEnum.find((item) => item.value === timeframeVal)?.label
                return label || this.referral?.customDate || 'N/A'
            },
            insurance() {
                if (!this.referralData) return 'N/A'
                return (
                    this.referralData?.insurancePlan?.name ||
                    this.referralData?.insurance?.name ||
                    this.referralData?.insurancePlan ||
                    this.referralData?.insurance ||
                    'N/A'
                )
            },
        },

        methods: {
            isAncillary(referral) {
                if (referral?.facilityServiceTests && referral.facilityServiceTests.length > 0) {
                    return true
                }
                if (referral?.speciality || referral?.subSpeciality) {
                    return false
                }
                return referral?.stageInfo?.targetPracticeType === PracticeTypeEnum.ANCILLARY
            },
            hasAdditionalTests(referral) {
                return this.isAncillary(referral) && (referral?.facilityServiceTests?.length || 0) > 1
            },
            getMatchReferralTitle(referral) {
                if (this.isAncillary(referral)) {
                    return referral?.facilityServiceTests?.[0]?.name || 'N/A'
                }

                return (
                    referral?.speciality?.name ||
                    referral?.subSpeciality?.name ||
                    (typeof referral?.speciality === 'string' ? referral.speciality : null) ||
                    referral?.diagnosticService?.name ||
                    'N/A'
                )
            },
            getAdditionalTestsCount(referral) {
                const count = referral?.facilityServiceTests?.length || 0
                return count > 1 ? count - 1 : 0
            },
            getAncillaryTestsTooltip(referral) {
                return referral?.facilityServiceTests?.map((test) => test.name)?.join(', ')
            },
            itemKey(item, index) {
                if (item.type === 'external') return `ext-${item.providerNpi || item.id || index}`
                return item.id != null ? item.id : `in-pool-${index}`
            },
            getReferralPoolProviders() {
                this.isLoading = true
                this.loadError = null
                ReferralsPoolAPI.getReferralsPoolProviders(this.referralPoolId, {
                    feasibility: true,
                })
                    .then((response) => {
                        this.providers = Array.isArray(response) ? response : Array.isArray(response?.data) ? response.data : []
                    })
                    .catch(() => {
                        this.loadError = true
                        this.providers = []
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            },
            getAvatarUser(provider) {
                if (provider.type === 'external') {
                    const contact = provider.data?.contactInfo || {}
                    return {
                        firstName: contact.firstName,
                        lastName: contact.lastName,
                        name: contact.firstName || contact.lastName ? undefined : contact.organizationName,
                        avatar: null,
                    }
                }

                return provider
            },
            getProviderName(provider) {
                if (!provider) return ''
                if (provider.type === 'external') {
                    const contact = provider.data?.contactInfo || {}
                    const fullName = [contact.firstName, contact.lastName].filter(Boolean).join(' ')
                    if (fullName && contact.designation) {
                        return `${fullName}, ${contact.designation}`
                    }
                    return fullName || contact.organizationName || ''
                }

                const fullName = [provider.firstName, provider.lastName].filter(Boolean).join(' ')
                if (fullName && provider.designation) {
                    return `${fullName}, ${provider.designation}`
                }
                if (fullName) return fullName

                if (provider.name && provider.designation) {
                    return `${provider.name}, ${provider.designation}`
                }
                return provider.name || provider.practice?.name || ''
            },
            getProviderSpeciality(provider) {
                if (provider?.type === 'external') {
                    return provider?.speciality || ''
                }
                return provider.specialities?.[0]?.speciality?.name || ''
            },

            getStageClass(referral) {
                if (this.hasPendingAdditionalRecordsRequest(referral)) {
                    return 'referral-tracking__referrals diagnosis'
                }
                const stage = referral?.stageInfo?.stage
                if (!stage) return 'referral-tracking__referrals gray'

                switch (stage) {
                    case StagesEnum.CONFIRMED:
                    case StagesEnum.AWAITING_PATIENT_VISIT:
                        return 'referral-tracking__referrals green'

                    case StagesEnum.REFERRAL_SUBMITTED:
                    case StagesEnum.AWAITING_PROVIDER_PROPOSALS:
                    case StagesEnum.AWAITING_REFERRER_SELECTION:
                    case StagesEnum.AWAITING_PROPOSALS:
                    case StagesEnum.PENDING_PROVIDER_CONFIRMATION:
                        return 'referral-tracking__referrals yellow'

                    case StagesEnum.PROPOSALS_CLOSED:
                        return 'referral-tracking__referrals purple'

                    case StagesEnum.SELECT_A_TIME_SLOT:
                    case StagesEnum.PROPOSALS_RECEIVED:
                    case StagesEnum.PROPOSE_TIME_SLOTS:
                        return 'referral-tracking__referrals blue'

                    case StagesEnum.CANCELLED_NO_PROPOSALS:
                    case StagesEnum.CANCELLED_NO_SELECTION:
                        return 'referral-tracking__referrals today'

                    default:
                        return 'referral-tracking__referrals gray'
                }
            },
            formatStage(referral) {
                let stage = referral?.stageInfo?.stage

                if (this.hasPendingAdditionalRecordsRequest(referral)) {
                    stage = StagesEnum.UPLOAD_RECORDS
                }
                if (!stage) return 'N/A'

                return stage
                    .toLowerCase()
                    .split('_')
                    .map((word) => (word.length > 2 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
                    .join(' ')
            },
            getStageTooltip(provider) {
                if (this.hasPendingAdditionalRecordsRequest(provider)) {
                    return NextActionsEnum.UPLOAD_RECORDS
                }
                return provider?.stageInfo?.nextAction
            },
            isStageClickable(provider) {
                const stage = provider?.stageInfo?.stage
                if (stage === StagesEnum.SELECT_A_TIME_SLOT || stage === StagesEnum.PROPOSALS_RECEIVED) {
                    return true
                }
                return false
            },
            isStageLoadingForReferral(provider) {
                return this.isStageLoading && this.selectedProviderForActions?.id === provider.id
            },
            handleStageClick(provider) {
                this.selectedProviderForActions = provider
                const stage = provider?.stageInfo?.stage
                switch (stage) {
                    case StagesEnum.SELECT_A_TIME_SLOT:
                    case StagesEnum.PROPOSALS_RECEIVED:
                        this.chooseSlotMethod({
                            ...provider,
                            referralId: this.referralPoolId,
                        })
                }
            },
            chooseSlotMethod(data) {
                this.$router.push({
                    name: `referrals.made.pool.show`,
                    params: {
                        id: data.referralId,
                    },
                })
            },
            getStageClasses(provider) {
                return [
                    this.getStageClass(provider),
                    {
                        'referral-tracking__referrals-clickable': this.isStageClickable(provider),
                        'referral-tracking__referrals-clickable--loading': this.isStageLoadingForReferral(provider),
                    },
                ]
            },

            handleStageAction(provider) {
                if (this.isStageClickable(provider)) {
                    this.handleStageClick(provider)
                }
            },

            hasPendingAdditionalRecordsRequest(provider) {
                return provider.additionalRecordsRequest?.some((request) => request.status === 'PENDING')
            },
            hasCompletedAdditionalRecordsRequest(provider) {
                return (
                    provider.additionalRecordsRequest?.length > 0 &&
                    provider.additionalRecordsRequest.every((request) => request.status === 'FULFILLED')
                )
            },
            getStatusText(referral) {
                const displayStatus = this.getDisplayStatus(referral?.status, ReferralTrackingTypeEnum.MATCH)

                return this.$t(`pages.referrals.tracking.status_title.${displayStatus}`)
            },
            getDisplayStatus(status, sourceType) {
                const statusMapping = {
                    [ReferralTrackingTypeEnum.MATCH]: {
                        [ReferralStatusEnum.CREATED]: 'CREATED',
                        [ReferralStatusEnum.TIME_SLOTS_PROPOSED]: 'AWAITING_CONFIRMATION',
                        [ReferralStatusEnum.DECLINED]: 'DECLINED',
                        [ReferralStatusEnum.SCHEDULED]: 'SCHEDULED',
                        [ReferralStatusEnum.INACTIVE]: 'EXPIRED',
                    },

                    [ReferralTrackingTypeEnum.DIRECT]: {
                        [ReferralStatusEnum.PATIENT_VISIT_PENDING]: 'SCHEDULED',
                        [ReferralStatusEnum.PATIENT_VISIT_PAST]: 'NO_SHOW',
                        [ReferralStatusEnum.NO_SHOW]: 'NO_SHOW',
                        [ReferralStatusEnum.CANCELLED]: 'CANCELLED',
                        [ReferralStatusEnum.CONSULTATION_NOTES_PENDING]: 'CONSULTATION_NOTES_PENDING',
                        [ReferralStatusEnum.COMPLETED]: 'COMPLETED',
                    },
                }

                return statusMapping[sourceType]?.[status] || status
            },
            getStatusColor(referral) {
                const displayStatus = this.getDisplayStatus(referral?.status, ReferralTrackingTypeEnum.MATCH)

                const yellowStatuses = ['CREATED', 'AWAITING_CONFIRMATION']

                const blueStatuses = ['SCHEDULED', 'COMPLETED']

                const redStatuses = ['DECLINED', 'CANCELLED', 'NO_SHOW', 'EXPIRED']

                const greenStatuses = ['CONSULTATION_NOTES_PENDING']

                if (yellowStatuses.includes(displayStatus)) return 'yellow'
                if (blueStatuses.includes(displayStatus)) return 'blue'
                if (redStatuses.includes(displayStatus)) return 'today'
                if (greenStatuses.includes(displayStatus)) return 'green'

                return 'gray'
            },
            getStatusBadgeClass(referral) {
                if (!referral) {
                    return 'referral-tracking__referrals gray'
                }

                return {
                    'referral-tracking__referrals': true,
                    'referral-tracking__status-badge': true,
                    [this.getStatusColor(referral)]: true,
                }
            },
            getTimingText(referral) {
                const stageInfo = referral?.stageInfo
                referral.sourceType = 'referrals_pool'
                if (!stageInfo) return 'N/A'
                if (referral?.sourceType === ReferralTrackingTypeEnum.MATCH) {
                    if (
                        referral.status === ReferralStatusEnum.SCHEDULED ||
                        referral.status === ReferralStatusEnum.DECLINED ||
                        referral.status === ReferralStatusEnum.INACTIVE
                    )
                        return 'N/A'
                    else {
                        const totalHours = stageInfo.hoursLeft

                        if (totalHours <= 0) {
                            return 'N/A'
                        }

                        if (totalHours < 1) {
                            const minutes = Math.ceil(totalHours * 60)
                            return `${minutes} minute${minutes !== 1 ? 's' : ''} left`
                        }

                        const wholeHours = Math.floor(totalHours)
                        const days = Math.floor(wholeHours / 24)
                        const hours = wholeHours % 24

                        if (days > 0 && hours > 0) {
                            return `${days} day${days > 1 ? 's' : ''} ${hours} hour${hours > 1 ? 's' : ''} left`
                        }

                        if (days > 0) {
                            return `${days} day${days > 1 ? 's' : ''} left`
                        }

                        return `${hours} hour${hours > 1 ? 's' : ''} left`
                    }
                } else if (referral?.sourceType === ReferralTrackingTypeEnum.DIRECT) {
                    return 'N/A'
                }
            },

            getTimingClass(stageInfo) {
                if (!stageInfo) return 'referral-tracking__referrals gray'

                const { urgency, hoursLeft } = stageInfo

                const isThreeHoursLeft = hoursLeft >= 0 && hoursLeft <= 3

                return {
                    'referral-tracking__referrals': true,

                    gray: hoursLeft <= 0,

                    green: urgency === UrgencyEnum.LOW && hoursLeft > 3,

                    yellow: urgency === UrgencyEnum.MEDIUM && hoursLeft > 3,

                    today: isThreeHoursLeft || ([UrgencyEnum.HIGH, UrgencyEnum.CRITICAL].includes(urgency) && hoursLeft > 3),
                }
            },
            openDetailsModal(sourceType) {
                this.isFetching = true
                this.showViewDetailsModal = true
                if (sourceType === ReferralTrackingTypeEnum.DIRECT) {
                    ReferralsAPI.get(this.activeReferral?.referralId, this.$auth.getActiveMember()?.id || this.$auth.getUser().id)
                        .then((res) => {
                            this.activeReferral = res
                        })
                        .finally(() => (this.isFetching = false))
                } else {
                    if (this.$auth.getPractice().practiceType === PracticeTypeEnum.PHYSICIAN) {
                        ReferralsPoolAPI.getReferralPoolById(this.referralPoolId, 'made', this.$auth.getActiveMember()?.id)
                            .then((res) => {
                                this.activeReferral = res
                            })
                            .finally(() => (this.isFetching = false))
                    } else {
                        ReferralsPoolAPI.getReferralPoolById(this.referralPoolId, 'made', null, this.$auth.getPractice()?.id)
                            .then((res) => {
                                this.activeReferral = res
                            })
                            .finally(() => (this.isFetching = false))
                    }
                }
            },
            closeDetailsModal() {
                this.showViewDetailsModal = false
                this.activeReferral = {}
            },
            openDirectReferralFromMatchReferal() {
                this.showViewDetailsModal = false

                this.$nextTick(() => {
                    this.openDetailsModal('referral')
                })
            },
            refreshMatchReferral(referralId) {
                const index = this.referrals.findIndex((r) => r.id === referralId)

                if (index === -1) return

                const existingSourceType = this.referrals[index]?.sourceType

                const request =
                    this.$auth.getPractice().practiceType === PracticeTypeEnum.PHYSICIAN
                        ? ReferralsPoolAPI.getReferralPoolById(
                              referralId,
                              this.activeReferralTab,
                              this.$auth.getActiveMember()?.id,
                          )
                        : ReferralsPoolAPI.getReferralPoolById(
                              referralId,
                              this.activeReferralTab,
                              null,
                              this.$auth.getPractice()?.id,
                          )

                request.then((res) => {
                    res.sourceType = existingSourceType
                    this.$set(this.referrals, index, res)
                })
            },
            uploadRecords(provider) {
                this.selectedProvider = provider
                this.showUploadRecordsModal = true
            },
            closeUploadRecordsModal() {
                this.showUploadRecordsModal = false
                this.selectedProvider = null
            },
            handleRecordsUploaded() {
                this.closeUploadRecordsModal()
                this.emitEvent({ name: 'records-uploaded', payload: this.referralPoolId })
            },
        },

        mounted() {
            this.getReferralPoolProviders()
        },
    }
</script>

<style scoped lang="scss">
    .content {
        max-height: 500px;
        overflow-y: auto;

        &__error {
            padding: 20px;
            text-align: center;
            color: var(--color-error, #c00);
        }
    }

    .referral-pool-details-card {
        background: #f9fafb;
        border: 1px solid #eef2f6;
        border-radius: 8px;
        padding: 16px 24px;
        margin: 0 20px 20px 20px;
    }

    .referral-pool-details-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        column-gap: 48px;
        row-gap: 12px;

        @media (max-width: 768px) {
            grid-template-columns: 1fr;
            column-gap: 0;
            row-gap: 8px;
        }
    }

    .referral-pool-details-col {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    .detail-row {
        display: flex;
        align-items: baseline;
        font-size: 14px;
        line-height: 1.5;
    }

    .detail-label {
        color: #4b5563;
        font-weight: 600;
        margin-right: 8px;
        flex-shrink: 0;
    }

    .detail-value {
        color: #111827;
        font-weight: 500;
        word-break: break-word;
    }
</style>
